/**
 * The system map's layout (docs/systems.md "Integrations"), pure and unit-testable, in the visual language of the Now
 * map (map/layout.ts): systems as cards in columns by the direction data flows (sources left, sinks right, the hubs
 * between), the integrations as orthogonal lines with short rounded corners. A line leaves its source from the side it
 * flows to, turns once in the gap between two columns on a vertical track of its own and enters its target from the
 * side it comes from; a line that must pass a column travels along a free corridor between the cards; a line against
 * the flow (a delivery confirmation back to the ERP) leaves from the left and enters from the right through the same
 * gaps. The tracks of a gap are ordered so that as few lines as possible cross (exactly, over every order, when the gap
 * is not crowded), and share a track only where their runs are far apart. Where a line meets a card, its end has its own
 * port. Every pair of systems is one line, whatever the number of processes. A label sits on a straight run of its own
 * line, clear of the cards, the other labels and the other lines; when no run has room for the whole text it says less
 * (the full text is in the tooltip). The systems connected to the main picture fill the width; small separate groups
 * (FAC → ESG, HR → PAY) sit side by side in a band below, the systems without lines between them in a quiet row last.
 * Nothing here touches the DOM: text is measured by `measure` when the renderer has one, by an estimate otherwise.
 */
import { namesTechnical } from './namemode.ts';
import type { Integration } from './api.ts';
import { count } from './format.ts';
import { estimate, type Font, type Measure } from '../map/layout.ts';

// ---------------------------------------------------------------- dimensions

export const NODE_H = 56;
export const LOOSE_H = 44;
export const LABEL_H = 18;
/** The radius of a corner, as on the Now map. */
export const CORNER = 10;
const PAD_X = 24;
const PAD_Y = 12;
const NODE_W_MIN = 176;
const NODE_W_MAX = 232;
const NODE_W_TIGHT = 156;
const ROW_GAP = 32;
/** Between two columns: room for the tracks and a label on each side. */
const GAP_MIN = 168;
/** A sparse map does not stretch a line past this; the picture is centred instead. */
const GAP_MAX = 900;
/** Outside the last column, when a line between two systems of that column needs a track. */
const OUTER = 64;
const TRACK = 10;
/** A track keeps this far from the cards on either side of its gap. */
const WALL = CORNER + 14;
const PORT_STEP = 10;
const PORT_WIDE = 22;
const LABEL_PAD = 8;
/** Air between a label and a card or another label. */
const AIR = 4;
const BAND_GAP = 44;
const CAPTION_H = 22;
const GROUP_GAP = 72;
const LOOSE_GAP = 12;
/** Ends of a line closer than this on one track are kept apart (one corner each and some air). */
const SHARE_SEP = 2 * CORNER + 28;

const NAME_F: Font = { size: 12, weight: 500, mono: false };
const NUM_F: Font = { size: 11, weight: 500, mono: true };
const TITLE_F: Font = { size: 14, weight: 600, mono: false };
const META_F: Font = { size: 11, weight: 500, mono: true };
const SEP = ' · ';

// ---------------------------------------------------------------- types

export type Pt = { x: number; y: number };
export type Rect = { x: number; y: number; w: number; h: number };

/** One pair of systems and the processes carrying data between them. */
export interface SysPair {
  key: string; from: string; to: string;
  /** The processes along it, the failing first, then the busiest. */
  items: Integration[];
  runs: number; failed: number;
  /** failing: one of its processes is; quiet: all of them are. */
  status: Integration['status'];
}
export interface SysNode extends Rect {
  code: string;
  /** The column (-1: the quiet row). */
  layer: number;
  part: 'main' | 'band' | 'loose';
  /** Processes on its lines that are failing. */
  failing: number;
  /** Lines touching it. */
  degree: number;
}
export interface SysLabel extends Rect {
  /** The process (or "3 processes"), possibly shortened or left out; and the runs. */
  name: string;
  num: string;
  /** The whole text, for the tooltip when the label says less. */
  full: string;
  /** 0: the whole text … 4: the runs only. */
  variant: number;
  /** On its own line (true) or just above it. */
  onLine: boolean;
}
export interface SysEdge extends SysPair {
  /** Against the flow (or between two systems of one column). */
  back: boolean;
  /** Corner points, the SVG path with rounded corners, and the stroke width (by runs). */
  pts: Pt[];
  d: string;
  stroke: number;
  label: SysLabel;
}
export interface SysCaption { x: number; y: number; text: string }
export interface SysLayout {
  width: number; height: number;
  nodes: SysNode[];
  edges: SysEdge[];
  captions: SysCaption[];
  /** Per system, the processes without a line: one-sided (reads or writes only) and within it. */
  lone: Map<string, Integration[]>;
}
export interface SysLayoutOptions {
  /** The width there is (the map grows past it, and scrolls, only when the columns do not fit). */
  width: number;
  measure?: Measure;
  /** A system's name as the card shows it (its width follows); the code by default. */
  name?: (code: string) => string;
}

// ---------------------------------------------------------------- helpers

const keyOf = (from: string, to: string) => `${from}>${to}`;
const statusRank = (i: Integration) => (i.status === 'failing' ? 0 : 1);
const byImportance = (a: Integration, b: Integration) => statusRank(a) - statusRank(b) || b.runs - a.runs || a.process.localeCompare(b.process);
const round = (v: number) => Math.round(v * 10) / 10;
export const hits = (a: Rect, b: Rect, air = 0) =>
  a.x < b.x + b.w + air && b.x < a.x + a.w + air && a.y < b.y + b.h + air && b.y < a.y + a.h + air;
/** The process without its [PREFIX]. */
export const processTitle = (p: string) => (namesTechnical() ? p : p.replace(/^\s*\[[^\]]{1,24}\]\s*/, ''));
/** What a line is called: its process, or how many there are. */
export const pairTitle = (p: Pick<SysPair, 'items'>) => (p.items.length === 1 ? processTitle(p.items[0]!.process) : `${p.items.length} processes`);
const runsWord = (n: number) => `${count(n)} ${n === 1 ? 'run' : 'runs'}`;

/** Corner points → an SVG path whose corners are quarter circles (never more than half of either leg). */
export function roundedPath(pts: Pt[], radius = CORNER): string {
  let d = `M${round(pts[0]!.x)},${round(pts[0]!.y)}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const A = pts[i - 1]!;
    const P = pts[i]!;
    const B = pts[i + 1]!;
    const lA = Math.hypot(P.x - A.x, P.y - A.y);
    const lB = Math.hypot(B.x - P.x, B.y - P.y);
    const r = Math.min(radius, lA / 2, lB / 2);
    const din = { x: (P.x - A.x) / (lA || 1), y: (P.y - A.y) / (lA || 1) };
    const dout = { x: (B.x - P.x) / (lB || 1), y: (B.y - P.y) / (lB || 1) };
    const cross = din.x * dout.y - din.y * dout.x;
    if (r < 0.5 || Math.abs(cross) < 0.01) {
      d += ` L${round(P.x)},${round(P.y)}`;
      continue;
    }
    d += ` L${round(P.x - din.x * r)},${round(P.y - din.y * r)}`;
    d += ` A${round(r)},${round(r)} 0 0 ${cross > 0 ? 1 : 0} ${round(P.x + dout.x * r)},${round(P.y + dout.y * r)}`;
  }
  const last = pts[pts.length - 1]!;
  return `${d} L${round(last.x)},${round(last.y)}`;
}

/** Drops repeated and collinear middle points. */
function simplify(pts: Pt[]): Pt[] {
  const out: Pt[] = [];
  for (const p of pts) {
    const q = out[out.length - 1];
    if (q && Math.abs(q.x - p.x) < 0.01 && Math.abs(q.y - p.y) < 0.01) continue;
    const o = out[out.length - 2];
    if (o && q && ((Math.abs(o.x - q.x) < 0.01 && Math.abs(q.x - p.x) < 0.01) || (Math.abs(o.y - q.y) < 0.01 && Math.abs(q.y - p.y) < 0.01))) out.pop();
    out.push(p);
  }
  return out;
}

// ---------------------------------------------------------------- the pairs, the groups, the columns

/** One pair per two systems in one direction; what has no other system goes to `lone`. */
export function pairsOf(list: readonly Integration[]) {
  const byPair = new Map<string, Integration[]>();
  const lone = new Map<string, Integration[]>();
  const codes = new Set<string>();
  for (const i of list) {
    if (i.from) codes.add(i.from);
    if (i.to) codes.add(i.to);
    if (i.from && i.to && i.from !== i.to) {
      const k = keyOf(i.from, i.to);
      byPair.set(k, [...(byPair.get(k) ?? []), i]);
    } else {
      const c = (i.from ?? i.to)!;
      lone.set(c, [...(lone.get(c) ?? []), i]);
    }
  }
  for (const v of lone.values()) v.sort(byImportance);
  const pairs = [...byPair].map(([key, items]): SysPair => {
    const [from, to] = key.split('>') as [string, string];
    items.sort(byImportance);
    return {
      key, from, to, items,
      runs: items.reduce((n, i) => n + i.runs, 0),
      failed: items.reduce((n, i) => n + (i.status === 'failing' ? i.failed : 0), 0),
      status: items.some((i) => i.status === 'failing') ? 'failing' : items.every((i) => i.status === 'quiet') ? 'quiet' : 'ok',
    };
  }).sort((a, b) => a.key.localeCompare(b.key));
  return { pairs, lone, codes };
}

/**
 * Columns: the busier of two opposite directions counts, a depth-first walk from where data starts cuts cycles, the
 * longest path into a system is its column, and a source sits just before the first system it feeds (exchange rates
 * next to the ERP, not far left of it).
 */
function layersOf(pairs: SysPair[], nodes: string[]): Map<string, number> {
  const runsOf = new Map(pairs.map((p) => [p.key, p.runs]));
  const kept = pairs.filter((p) => {
    const other = runsOf.get(keyOf(p.to, p.from));
    return other === undefined || p.runs > other || (p.runs === other && p.from < p.to);
  });
  const outs = new Map<string, string[]>();
  for (const p of kept) outs.set(p.from, [...(outs.get(p.from) ?? []), p.to]);
  const flow = (c: string) => pairs.reduce((n, p) => n + (p.from === c ? p.runs : p.to === c ? -p.runs : 0), 0);
  const acyclic = new Map<string, string[]>();
  const state = new Map<string, 1 | 2>();
  const visit = (c: string) => {
    state.set(c, 1);
    for (const n of [...(outs.get(c) ?? [])].sort()) {
      if (state.get(n) === 1) continue;
      acyclic.set(c, [...(acyclic.get(c) ?? []), n]);
      if (!state.has(n)) visit(n);
    }
    state.set(c, 2);
  };
  for (const c of [...nodes].sort((a, b) => flow(b) - flow(a) || a.localeCompare(b))) if (!state.has(c)) visit(c);
  const ins = new Map<string, string[]>();
  for (const [src, ns] of acyclic) for (const n of ns) ins.set(n, [...(ins.get(n) ?? []), src]);
  const layer = new Map<string, number>();
  const longest = (c: string, depth = 0): number => {
    if (layer.has(c)) return layer.get(c)!;
    if (depth > 500) return 0;
    const l = Math.max(0, ...(ins.get(c) ?? []).map((s) => longest(s, depth + 1) + 1));
    layer.set(c, l);
    return l;
  };
  for (const c of nodes) longest(c);
  for (const c of nodes) {
    const targets = acyclic.get(c) ?? [];
    if (!(ins.get(c)?.length) && targets.length) layer.set(c, Math.max(0, Math.min(...targets.map((t) => layer.get(t)!)) - 1));
  }
  const min = Math.min(...nodes.map((c) => layer.get(c)!));
  for (const c of nodes) layer.set(c, layer.get(c)! - min);
  return layer;
}

/** Order within the columns: by the mean position of the neighbours, sweeps both ways; the order with fewest crossings wins. */
function orderColumns(codes: string[], layer: Map<string, number>, pairs: SysPair[], weight: (c: string) => number): string[][] {
  const L = Math.max(...codes.map((c) => layer.get(c)!)) + 1;
  const cols: string[][] = Array.from({ length: L }, () => []);
  for (const c of [...codes].sort((a, b) => weight(b) - weight(a) || a.localeCompare(b))) cols[layer.get(c)!]!.push(c);
  const nb = new Map<string, string[]>(codes.map((c) => [c, []]));
  for (const p of pairs) { nb.get(p.from)!.push(p.to); nb.get(p.to)!.push(p.from); }
  const pos = new Map<string, number>();
  const place = () => cols.forEach((col) => col.forEach((c, i) => pos.set(c, i - (col.length - 1) / 2)));
  const crossings = () => {
    let n = 0;
    const es = pairs.map((p) => {
      const [a, b] = layer.get(p.from)! <= layer.get(p.to)! ? [p.from, p.to] : [p.to, p.from];
      return { la: layer.get(a)!, lb: layer.get(b)!, pa: pos.get(a)!, pb: pos.get(b)! };
    });
    for (let i = 0; i < es.length; i++) for (let j = i + 1; j < es.length; j++) {
      const e = es[i]!;
      const f = es[j]!;
      if (e.la !== f.la || e.lb !== f.lb) continue;
      if ((e.pa - f.pa) * (e.pb - f.pb) < 0) n++;
    }
    return n;
  };
  place();
  let best = { cols: cols.map((c) => [...c]), n: crossings() };
  for (let sweep = 0; sweep < 8; sweep++) {
    const range = sweep % 2 === 0 ? [...cols.keys()] : [...cols.keys()].reverse();
    for (const l of range) {
      const avg = (c: string) => {
        const ns = nb.get(c)!.filter((n) => layer.get(n) !== l);
        return ns.length ? ns.reduce((s, n) => s + pos.get(n)!, 0) / ns.length : pos.get(c)!;
      };
      cols[l]!.sort((a, b) => avg(a) - avg(b) || pos.get(a)! - pos.get(b)! || a.localeCompare(b));
      place();
    }
    const n = crossings();
    if (n < best.n) best = { cols: cols.map((c) => [...c]), n };
  }
  return best.cols;
}

// ---------------------------------------------------------------- routing

type Side = 'L' | 'R';
interface End { y: number; side: Side; node: string }
/** One edge's stretch in one gap: where it comes in and goes out (each at a wall). */
interface GapRun { key: string; gap: number; ends: [End, End]; lo: number; hi: number; t: number; x: number }
interface Plan {
  pair: SysPair; a: SysNode; b: SysNode;
  srcSide: Side; tgtSide: Side;
  /** The gaps the edge turns in (one, or two around a corridor) and the corridor's height. */
  gaps: number[];
  corridor: number | null;
  /** The corridor runs at the source's port height (straight out) or the target's (straight in). */
  straight: 'src' | 'tgt' | null;
  p1: number; p2: number;
  runs: GapRun[];
}

interface Group { nodes: SysNode[]; pairs: SysPair[]; cols: string[][] }

/**
 * Routes the edges of one group of placed nodes. `colX(l)` is a column's left edge; `wall(g)` the left and right of
 * gap g (between column g and g + 1; the last gap lies outside the last column).
 */
function routeGroup(g: Group, nodeW: number, colX: (l: number) => number, gapW: (g: number) => number, labelW: (key: string) => number): { pts: Map<string, Pt[]>; back: Map<string, boolean> } {
  const at = new Map(g.nodes.map((n) => [n.code, n]));
  const cy = (n: SysNode) => n.y + n.h / 2;
  const wallL = (gap: number) => colX(gap) + nodeW;
  const corridors: { y: number; x0: number; x1: number }[] = [];
  const plans: Plan[] = [];
  for (const pair of g.pairs) {
    const a = at.get(pair.from)!;
    const b = at.get(pair.to)!;
    const la = a.layer;
    const lb = b.layer;
    let srcSide: Side, tgtSide: Side, gaps: number[], inter: number[] = [];
    if (lb === la + 1) { srcSide = 'R'; tgtSide = 'L'; gaps = [la]; }
    else if (lb === la) { srcSide = 'R'; tgtSide = 'R'; gaps = [la]; }
    else if (lb === la - 1) { srcSide = 'L'; tgtSide = 'R'; gaps = [lb]; }
    else if (lb > la) { srcSide = 'R'; tgtSide = 'L'; gaps = [la, lb - 1]; inter = range(la + 1, lb - 1); }
    else { srcSide = 'L'; tgtSide = 'R'; gaps = [la - 1, lb]; inter = range(lb + 1, la - 1); }
    let corridor: number | null = null;
    let straight: Plan['straight'] = null;
    if (inter.length) {
      // A corridor through the columns in between, where no card is: straight out of the source when its row is free
      const blocked = g.nodes.filter((n) => inter.includes(n.layer)).map((n) => [n.y - 14, n.y + n.h + 14] as const);
      const free = (y: number) => blocked.every(([lo, hi]) => y <= lo || y >= hi);
      const x0 = colX(Math.min(...inter));
      const x1 = colX(Math.max(...inter)) + nodeW;
      const clear = (y: number) => free(y) && corridors.every((c) => c.x1 < x0 || c.x0 > x1 || Math.abs(c.y - y) >= TRACK);
      if (clear(cy(a)) && Math.abs(cy(a) - cy(b)) > 1) { corridor = cy(a); straight = 'src'; }
      else if (clear(cy(b))) { corridor = cy(b); straight = 'tgt'; }
      else {
        const mid = (cy(a) + cy(b)) / 2;
        const cands: number[] = [];
        for (const [lo, hi] of blocked) cands.push(lo, hi);
        cands.push(mid);
        let bestY = NaN;
        for (const base of cands) for (let k = 0; k < 40; k++) for (const s of [1, -1]) {
          const y = base + s * k * TRACK;
          if (clear(y) && (Number.isNaN(bestY) || Math.abs(y - mid) < Math.abs(bestY - mid))) bestY = y;
        }
        corridor = bestY;
      }
      corridors.push({ y: corridor, x0, x1 });
    }
    plans.push({ pair, a, b, srcSide, tgtSide, gaps, corridor, straight, p1: cy(a), p2: cy(b), runs: [] });
  }

  // Ports: the ends on one side of a card, ordered by where they head, a step apart around the middle
  const sides = new Map<string, { plan: Plan; end: 'src' | 'tgt'; toward: number }[]>();
  for (const p of plans) {
    const towardSrc = p.corridor ?? cy(p.b);
    const towardTgt = p.corridor ?? cy(p.a);
    const ks = `${p.a.code}|${p.srcSide}`;
    const kt = `${p.b.code}|${p.tgtSide}`;
    sides.set(ks, [...(sides.get(ks) ?? []), { plan: p, end: 'src', toward: towardSrc }]);
    sides.set(kt, [...(sides.get(kt) ?? []), { plan: p, end: 'tgt', toward: towardTgt }]);
  }
  for (const [k, list] of sides) {
    const n = at.get(k.split('|')[0]!)!;
    list.sort((x, y) => x.toward - y.toward || x.plan.pair.key.localeCompare(y.plan.pair.key));
    // Two or three ends sit far enough apart for a label on each line; more close up to the step
    const step = list.length > 1 ? Math.min(list.length <= 3 ? PORT_WIDE : PORT_STEP, (n.h - 16) / (list.length - 1)) : 0;
    list.forEach((e, i) => {
      const y = cy(n) + (i - (list.length - 1) / 2) * step;
      if (e.end === 'src') e.plan.p1 = y; else e.plan.p2 = y;
    });
  }
  // A line with only a small step between its ends goes straight when one end is alone on its side
  for (const p of plans) {
    const d = p.p2 - p.p1;
    if (Math.abs(d) < 0.5 || Math.abs(d) > 2 * CORNER + 6 || p.corridor !== null) continue;
    const alone = (code: string, side: Side) => sides.get(`${code}|${side}`)!.length === 1;
    const within = (n: SysNode, y: number) => y >= n.y + 12 && y <= n.y + n.h - 12;
    if (alone(p.b.code, p.tgtSide) && within(p.b, p.p1)) p.p2 = p.p1;
    else if (alone(p.a.code, p.srcSide) && within(p.a, p.p2)) p.p1 = p.p2;
  }
  for (const p of plans) {
    if (p.straight === 'src') p.corridor = p.p1;
    if (p.straight === 'tgt') p.corridor = p.p2;
  }

  // The stretches in each gap
  const byGap = new Map<number, GapRun[]>();
  const mk = (key: string, gap: number, e0: End, e1: End) => {
    const run: GapRun = { key, gap, ends: [e0, e1], lo: Math.min(e0.y, e1.y), hi: Math.max(e0.y, e1.y), t: 0, x: 0 };
    byGap.set(gap, [...(byGap.get(gap) ?? []), run]);
    return run;
  };
  for (const p of plans) {
    const opp = (s: Side): Side => (s === 'L' ? 'R' : 'L');
    // the source's side of a card is the opposite wall of the gap: a card's right side is its gap's left wall
    const srcWall = opp(p.srcSide);
    const tgtWall = opp(p.tgtSide);
    const A = p.a.code;
    const B = p.b.code;
    if (p.corridor === null) p.runs = [mk(p.pair.key, p.gaps[0]!, { y: p.p1, side: srcWall, node: A }, { y: p.p2, side: tgtWall, node: B })];
    else {
      p.runs = [
        mk(p.pair.key, p.gaps[0]!, { y: p.p1, side: srcWall, node: A }, { y: p.corridor, side: opp(srcWall), node: '' }),
        mk(p.pair.key, p.gaps[1]!, { y: p.corridor, side: opp(tgtWall), node: '' }, { y: p.p2, side: tgtWall, node: B }),
      ];
    }
  }

  // Tracks: per gap, the order with the fewest crossings; runs far apart share a track
  for (const [gap, all] of byGap) {
    const runs = all.filter((r) => !(r.ends[0].side !== r.ends[1].side && Math.abs(r.ends[0].y - r.ends[1].y) < 0.5));
    for (const r of all) if (!runs.includes(r)) r.x = NaN; // straight across: no vertical
    if (!runs.length) continue;
    const order = orderRuns(runs);
    order.forEach((r, i) => {
      r.t = 0;
      for (let j = 0; j < i; j++) {
        const o = order[j]!;
        if (o.lo < r.hi + SHARE_SEP && r.lo < o.hi + SHARE_SEP) r.t = Math.max(r.t, o.t + 1);
      }
    });
    const T = Math.max(...order.map((r) => r.t)) + 1;
    const left = wallL(gap);
    const w = gapW(gap);
    const step = T > 1 ? Math.min(TRACK, Math.max(4, (w - 2 * WALL) / (T - 1))) : 0;
    const block = (T - 1) * step;
    // The tracks lean toward the side with fewer cards, so the lines on the busy side have long straight runs for labels
    const lefts = new Set(runs.flatMap((r) => r.ends.filter((e) => e.side === 'L' && e.node).map((e) => e.node)));
    const rights = new Set(runs.flatMap((r) => r.ends.filter((e) => e.side === 'R' && e.node).map((e) => e.node)));
    // ... at least as long as the longest label there asks, when the gap has room
    const need = Math.max(0, ...runs.map((r) => labelW(r.key))) + 2 * CORNER + 12;
    const want = lefts.size > rights.size ? left + Math.max(0.62 * w, need)
      : rights.size > lefts.size ? left + w - Math.max(0.62 * w, need) - block
        : left + w / 2 - block / 2;
    const start = Math.max(left + WALL, Math.min(left + w - WALL - block, want));
    for (const r of order) r.x = start + r.t * step;
  }

  // The corner points
  const pts = new Map<string, Pt[]>();
  const back = new Map<string, boolean>();
  for (const p of plans) {
    const sx = p.srcSide === 'R' ? p.a.x + p.a.w : p.a.x;
    const tx = p.tgtSide === 'L' ? p.b.x - 2 : p.b.x + p.b.w + 2;
    const out: Pt[] = [{ x: sx, y: p.p1 }];
    const [r0, r1] = p.runs;
    if (p.corridor === null) {
      if (!Number.isNaN(r0!.x)) out.push({ x: r0!.x, y: p.p1 }, { x: r0!.x, y: p.p2 });
    } else {
      if (!Number.isNaN(r0!.x)) out.push({ x: r0!.x, y: p.p1 }, { x: r0!.x, y: p.corridor });
      if (!Number.isNaN(r1!.x)) out.push({ x: r1!.x, y: p.corridor }, { x: r1!.x, y: p.p2 });
    }
    out.push({ x: tx, y: p.p2 });
    pts.set(p.pair.key, simplify(out));
    back.set(p.pair.key, p.b.layer <= p.a.layer);
  }
  return { pts, back };
}

function range(a: number, b: number): number[] {
  const out: number[] = [];
  for (let i = a; i <= b; i++) out.push(i);
  return out;
}

/**
 * The order of the tracks in a gap, left to right, with the fewest crossings: placing A left of B, a horizontal of A
 * toward the right wall crosses B's vertical when its height is within B's, a horizontal of B toward the left wall
 * crosses A's; and two horizontals at one height running over each other is far worse than a crossing.
 */
function orderRuns(runs: GapRun[]): GapRun[] {
  const n = runs.length;
  const inside = (y: number, r: GapRun) => y > r.lo + 0.5 && y < r.hi - 0.5;
  const C = runs.map((A) => runs.map((B) => {
    if (A === B) return 0;
    let c = 0;
    for (const e of A.ends) if (e.side === 'R' && inside(e.y, B)) c++;
    for (const e of B.ends) if (e.side === 'L' && inside(e.y, A)) c++;
    for (const ea of A.ends) for (const eb of B.ends) if (ea.side === 'R' && eb.side === 'L' && Math.abs(ea.y - eb.y) < 3) c += 100;
    return c;
  }));
  const heuristic = [...runs.keys()].sort((i, j) => {
    const a = runs[i]!;
    const b = runs[j]!;
    return (a.lo + a.hi) - (b.lo + b.hi) || a.key.localeCompare(b.key);
  });
  if (n > 14) {
    // Greedy: each run where it adds the least
    const order: number[] = [];
    for (const i of heuristic) {
      let bestAt = 0;
      let bestC = Infinity;
      for (let at = 0; at <= order.length; at++) {
        let c = 0;
        order.forEach((j, k) => { c += k < at ? C[j]![i]! : C[i]![j]!; });
        if (c < bestC) { bestC = c; bestAt = at; }
      }
      order.splice(bestAt, 0, i);
    }
    return order.map((i) => runs[i]!);
  }
  // Exact: the cheapest order over every subset (the runs placed so far are left of the next)
  const size = 1 << n;
  const best = new Float64Array(size).fill(Infinity);
  const from = new Int32Array(size).fill(-1);
  best[0] = 0;
  for (let mask = 0; mask < size; mask++) {
    if (best[mask] === Infinity) continue;
    for (const i of heuristic) {
      if (mask & (1 << i)) continue;
      let c = best[mask]!;
      for (let j = 0; j < n; j++) if (mask & (1 << j)) c += C[j]![i]!;
      const next = mask | (1 << i);
      if (c < best[next]! - 1e-9) { best[next] = c; from[next] = i; }
    }
  }
  const order: number[] = [];
  let mask = size - 1;
  while (mask) { const i = from[mask]!; order.unshift(i); mask &= ~(1 << i); }
  return order.map((i) => runs[i]!);
}

// ---------------------------------------------------------------- the layout

/**
 * The system map for a width: deterministic (the same integrations and width, the same picture), every pair of
 * systems one edge, nothing overlapping (see `problems`).
 */
export function layoutSystems(list: readonly Integration[], options: SysLayoutOptions): SysLayout {
  const measure = options.measure ?? estimate;
  const nameOf = options.name ?? ((c: string) => c);
  const { pairs, lone, codes } = pairsOf(list);
  const avail = Math.max(320, Math.floor(options.width));

  // Groups of connected systems, the biggest first; systems without a line apart
  const nb = new Map<string, Set<string>>();
  for (const p of pairs) {
    (nb.get(p.from) ?? nb.set(p.from, new Set()).get(p.from)!).add(p.to);
    (nb.get(p.to) ?? nb.set(p.to, new Set()).get(p.to)!).add(p.from);
  }
  const seen = new Set<string>();
  const groupCodes: string[][] = [];
  for (const c of [...nb.keys()].sort()) {
    if (seen.has(c)) continue;
    const g: string[] = [];
    const stack = [c];
    seen.add(c);
    while (stack.length) {
      const x = stack.pop()!;
      g.push(x);
      for (const n of [...nb.get(x)!].sort()) if (!seen.has(n)) { seen.add(n); stack.push(n); }
    }
    groupCodes.push(g.sort());
  }
  const runsOfGroup = (g: string[]) => pairs.filter((p) => g.includes(p.from)).reduce((n, p) => n + p.runs, 0);
  groupCodes.sort((a, b) => b.length - a.length || runsOfGroup(b) - runsOfGroup(a) || a[0]!.localeCompare(b[0]!));
  const loose = [...codes].filter((c) => !nb.has(c)).sort();

  const maxRuns = Math.max(1, ...pairs.map((p) => p.runs));
  const failingOf = (c: string) => pairs.filter((p) => p.from === c || p.to === c).reduce((n, p) => n + p.items.filter((i) => i.status === 'failing').length, 0);
  const degree = (c: string) => pairs.filter((p) => p.from === c || p.to === c).length;
  const weight = (c: string) => pairs.reduce((n, p) => n + (p.from === c || p.to === c ? p.runs : 0), 0);

  // The width of a card: its name in one line beside the badge, within bounds
  const meta = (c: string) => `${c}${(lone.get(c)?.length ?? 0) > 0 ? ` · ${lone.get(c)!.length} more process` : ''}`;
  const need = Math.max(0, ...[...codes].map((c) => Math.max(measure(nameOf(c), TITLE_F), measure(meta(c), META_F)) + 12 + 30 + 10 + 14));
  let nodeW = Math.round(Math.max(NODE_W_MIN, Math.min(NODE_W_MAX, need)));

  const labelsOf = (p: SysPair) => variantsOf(p, measure);
  const nodes: SysNode[] = [];
  const pointsOf = new Map<string, Pt[]>();
  const backOf = new Map<string, boolean>();
  const captions: SysCaption[] = [];
  let width = avail;
  let cursor = PAD_Y;

  const layoutGroup = (codesIn: string[], part: 'main' | 'band', x0: number, gap: number, nodeWidth: number, lay: Map<string, number>, cols: string[][]) => {
    const own = pairs.filter((p) => codesIn.includes(p.from));
    const L = cols.length;
    const outer = own.some((p) => lay.get(p.from) === L - 1 && lay.get(p.to) === L - 1) ? OUTER : 0;
    const colX = (l: number) => x0 + l * (nodeWidth + gap);
    const gapW = (g: number) => (g >= L - 1 ? outer : gap);
    const rows = Math.max(...cols.map((c) => c.length));
    const h = rows * NODE_H + (rows - 1) * ROW_GAP;
    const placed: SysNode[] = [];
    cols.forEach((col, l) => {
      const ch = col.length * NODE_H + (col.length - 1) * ROW_GAP;
      col.forEach((c, i) => placed.push({
        code: c, layer: l, part, x: colX(l), y: (h - ch) / 2 + i * (NODE_H + ROW_GAP), w: nodeWidth, h: NODE_H,
        failing: failingOf(c), degree: degree(c),
      }));
    });
    const routed = routeGroup({ nodes: placed, pairs: own, cols }, nodeWidth, colX, gapW, (k) => labelsOf(own.find((p) => p.key === k)!)[0]!.w);
    // Shift so the top of what is drawn (a corridor may run above the cards) is at the cursor
    let top = 0;
    let bottom = h;
    for (const ps of routed.pts.values()) for (const p of ps) { top = Math.min(top, p.y - LABEL_H - 4); bottom = Math.max(bottom, p.y + LABEL_H + 4); }
    const dy = cursor - top;
    for (const n of placed) { n.y += dy; nodes.push(n); }
    for (const [k, ps] of routed.pts) { pointsOf.set(k, ps.map((p) => ({ x: p.x, y: p.y + dy }))); backOf.set(k, routed.back.get(k)!); }
    return { w: L * nodeWidth + (L - 1) * gap + outer, h: bottom - top };
  };

  const prepared = groupCodes.map((g) => {
    const own = pairs.filter((p) => g.includes(p.from));
    const lay = layersOf(own, g);
    const cols = orderColumns(g, lay, own, weight);
    return { g, own, lay, cols };
  });
  const mainIdx = prepared.length ? 0 : -1;
  // A small separate group goes to the band when there is a bigger picture above it
  const isBand = (i: number) => i > mainIdx && prepared[i]!.g.length <= Math.max(3, Math.floor(prepared[0]!.g.length / 2));
  const mainParts = prepared.filter((_, i) => i === mainIdx || !isBand(i));
  const bandParts = prepared.filter((_, i) => i !== mainIdx && isBand(i));

  // The main picture (and any other big group) fills the width: the gaps grow, the cards a little
  let mainLeft = PAD_X;
  for (const part of mainParts) {
    const L = part.cols.length;
    const outer = part.own.some((p) => part.lay.get(p.from) === L - 1 && part.lay.get(p.to) === L - 1) ? OUTER : 0;
    let w = nodeW;
    let gap = L > 1 ? (avail - 2 * PAD_X - outer - L * w) / (L - 1) : 0;
    // Room for the whole labels comes before wide cards: the cards narrow (a long name ends in …) before a label says less
    const desired = Math.max(GAP_MIN, Math.min(380, Math.max(0, ...part.own.map((p) => labelsOf(p)[0]!.w)) + 3 * WALL));
    if (L > 1 && gap < desired) {
      w = Math.max(NODE_W_MIN, Math.min(nodeW, Math.floor((avail - 2 * PAD_X - outer - (L - 1) * desired) / L)));
      gap = (avail - 2 * PAD_X - outer - L * w) / (L - 1);
      nodeW = Math.min(nodeW, w);
    }
    if (L > 1 && gap < GAP_MIN) {
      w = Math.max(NODE_W_TIGHT, Math.min(nodeW, Math.floor((avail - 2 * PAD_X - outer - (L - 1) * GAP_MIN) / L)));
      gap = Math.max(GAP_MIN, (avail - 2 * PAD_X - outer - L * w) / (L - 1));
      nodeW = Math.min(nodeW, w);
    }
    gap = Math.min(gap, GAP_MAX);
    const total = L * w + (L - 1) * gap + outer;
    const x0 = Math.max(PAD_X, (avail - total) / 2);
    if (part === mainParts[0]) mainLeft = x0;
    const r = layoutGroup(part.g, 'main', x0, gap, w, part.lay, part.cols);
    width = Math.max(width, x0 + r.w + PAD_X);
    cursor += r.h + BAND_GAP;
  }

  // The band: small groups side by side, as compact as their labels allow, wrapping
  if (bandParts.length) {
    captions.push({ x: mainLeft, y: cursor - 6, text: 'Separate flows' });
    cursor += CAPTION_H;
    // One gap for every group (the cards line up), as wide as the longest label asks; side by side when they fit
    const gap = Math.max(GAP_MIN, Math.min(420, Math.max(0, ...bandParts.flatMap((part) => part.own.map((p) => labelsOf(p)[0]!.w))) + 2 * WALL + 2 * LABEL_PAD));
    const right = Math.max(width, avail) - PAD_X;
    let x = mainLeft;
    let rowH = 0;
    for (const part of bandParts) {
      const L = part.cols.length;
      const gw = L * nodeW + (L - 1) * gap;
      if (x > mainLeft && x + gw > right + 1) { x = mainLeft; cursor += rowH + 24; rowH = 0; }
      const r = layoutGroup(part.g, 'band', x, gap, nodeW, part.lay, part.cols);
      width = Math.max(width, x + r.w + PAD_X);
      rowH = Math.max(rowH, r.h);
      x += r.w + GROUP_GAP;
    }
    cursor += rowH + BAND_GAP;
  }

  // The systems without a line: a quiet row, wrapping
  if (loose.length) {
    if (nodes.length) {
      captions.push({ x: mainLeft, y: cursor - 6, text: 'No flows to other systems' });
      cursor += CAPTION_H;
    }
    let x = mainLeft;
    for (const c of loose) {
      if (x > mainLeft && x + nodeW > Math.max(width, avail) - PAD_X) { x = mainLeft; cursor += LOOSE_H + LOOSE_GAP; }
      nodes.push({ code: c, layer: -1, part: 'loose', x, y: cursor, w: nodeW, h: LOOSE_H, failing: failingOf(c), degree: 0 });
      x += nodeW + LOOSE_GAP;
    }
    cursor += LOOSE_H + BAND_GAP;
  }
  const height = Math.max(NODE_H + 2 * PAD_Y, cursor - BAND_GAP + PAD_Y + 4);

  const edges: SysEdge[] = pairs.map((p) => {
    const pts = pointsOf.get(p.key)!;
    return {
      ...p, back: backOf.get(p.key)!, pts, d: roundedPath(pts),
      stroke: p.status === 'failing' ? 1.75 : 1.5 + 1.5 * Math.sqrt(p.runs / maxRuns),
      label: { x: 0, y: 0, w: 0, h: LABEL_H, name: '', num: '', full: '', variant: 0, onLine: true },
    };
  });
  placeLabels(edges, nodes, width, height, labelsOf, (p, w) => fitted(p, w, measure));
  return { width, height, nodes, edges, captions, lone };
}

// ---------------------------------------------------------------- labels

interface Variant { name: string; num: string; w: number }

/** What a label can say, from the most to the least. */
function variantsOf(p: SysPair, measure: Measure): Variant[] {
  const title = pairTitle(p);
  const runs = runsWord(p.runs);
  const num = p.status === 'failing' && p.failed > 0 ? `${runs} · ${count(p.failed)} failed` : runs;
  const short = (t: string, n: number) => (t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t);
  const width = (name: string, n: string) => Math.ceil((name ? measure(name, NAME_F) + measure(SEP, NUM_F) : 0) + measure(n, NUM_F)) + 2 * LABEL_PAD;
  const raw: [string, string][] = [[title, num], [title, count(p.runs)], [short(title, 20), count(p.runs)], ['', num], ['', count(p.runs)]];
  return raw.map(([name, n]) => ({ name, num: n, w: width(name, n) }));
}

/** The name cut (with …) to what fits `maxW` beside the runs; null when not even a few letters would. */
function fitted(p: SysPair, maxW: number, measure: Measure): Variant | null {
  const title = pairTitle(p);
  const num = count(p.runs);
  const base = Math.ceil(measure(SEP, NUM_F) + measure(num, NUM_F)) + 2 * LABEL_PAD;
  const roomFor = maxW - base;
  if (measure(title, NAME_F) <= roomFor) return { name: title, num, w: Math.ceil(base + measure(title, NAME_F)) };
  for (let n = title.length - 1; n >= 8; n--) {
    const name = `${title.slice(0, n).trimEnd()}…`;
    const w = measure(name, NAME_F);
    if (w <= roomFor) return { name, num, w: Math.ceil(base + w) };
  }
  return null;
}

/**
 * Each label on a straight run of its own line, clear of the cards, the other labels and, as far as there is room,
 * the other lines; the failing first, then the longest. A label that fits nowhere whole says less.
 */
function placeLabels(edges: SysEdge[], nodes: SysNode[], width: number, height: number, variantsFor: (p: SysPair) => Variant[], fit: (p: SysPair, maxW: number) => Variant | null): void {
  const segsOf = (e: SysEdge) => segments(e.pts).map((s) => ({ ...s, key: e.key }));
  const allSegs = edges.flatMap(segsOf);
  const ends = edges.map((e) => {
    const E = e.pts[e.pts.length - 1]!;
    return { key: e.key, r: { x: E.x - 12, y: E.y - 6, w: 14, h: 12 } };
  });
  const placed: Rect[] = [];
  const order = [...edges].sort((a, b) => Number(b.status === 'failing') - Number(a.status === 'failing') || variantsFor(b)[0]!.w - variantsFor(a)[0]!.w || a.key.localeCompare(b.key));
  for (const e of order) {
    const vs = variantsFor(e);
    const own = segments(e.pts);
    const lastIdx = own.length - 1;
    type Cand = { r: Rect; v: number; vv: Variant; onLine: boolean; cost: number; soft: number };
    const tiers: [number, boolean][] = [[0, true], [1, true], [2, true], [0, false], [1, false], [2, false], [3, true], [4, true], [3, false], [4, false]];
    let chosen: Cand | null = null;
    let fallback: Cand | null = null;
    const byLen = own.map((s, i) => ({ s, i })).filter(({ s }) => s.horizontal).sort((a, b) => b.s.len - a.s.len);
    for (const [ti, [v, onLine]] of tiers.entries()) {
      let bestHere: Cand | null = null;
      for (const [rank, { s, i }] of byLen.entries()) {
        const padA = i === 0 ? 6 : CORNER + 4;
        const padB = i === lastIdx ? 16 : CORNER + 4;
        const lo = Math.min(s.x0, s.x1);
        const hi = Math.max(s.x0, s.x1);
        const startPad = s.x1 >= s.x0 ? padA : padB;
        const endPad = s.x1 >= s.x0 ? padB : padA;
        // The third tier cuts the name to this run's length
        const vv = v === 2 ? fit(e, hi - endPad - (lo + startPad)) : vs[v]!;
        if (!vv) continue;
        const w = vv.w;
        const room = hi - endPad - (lo + startPad) - w;
        if (room < 0) continue;
        const steps = Math.max(1, Math.floor(room / 6));
        for (let k = 0; k <= steps; k++) {
          const frac = k / steps;
          const x = lo + startPad + room * frac;
          for (const dy of onLine ? [0] : [-(LABEL_H + 3), LABEL_H + 3]) {
            const r = { x, y: s.y0 - LABEL_H / 2 + dy, w, h: LABEL_H };
            if (r.x < 2 || r.x + r.w > width - 2 || r.y < 1 || r.y + r.h > height - 1) continue;
            if (nodes.some((n) => hits(r, n, AIR)) || placed.some((p) => hits(r, p, AIR))) continue;
            const soft = allSegs.filter((g) => g.key !== e.key && hits(r, g.box)).length + ends.filter((x) => x.key !== e.key && hits(r, x.r)).length * 2
              + (onLine ? 0 : own.filter((g, gi) => gi !== i && hits(r, g.box)).length);
            const cost = rank * 3 + Math.abs(frac - 0.5) * 6 + (dy > 0 ? 0.5 : 0);
            const c: Cand = { r, v, vv, onLine, cost, soft };
            if (soft === 0 && (!bestHere || cost < bestHere.cost)) bestHere = c;
            if (!fallback || soft * 100 + ti * 10 + cost < fallback.soft * 100 + tiers.findIndex(([tv, to]) => tv === fallback!.v && to === fallback!.onLine) * 10 + fallback.cost) fallback = c;
          }
        }
      }
      if (bestHere) { chosen = bestHere; break; }
    }
    const c = chosen ?? fallback;
    if (c) {
      const v = c.vv;
      e.label = { ...c.r, name: v.name, num: v.num, full: `${pairTitle(e)}${SEP}${vs[3]!.num}`, variant: c.v, onLine: c.onLine };
      placed.push(c.r);
    } else {
      // No straight run long enough (a very crowded map): the runs only, at the start of the line
      const v = vs[4]!;
      const s = e.pts[0]!;
      e.label = { x: s.x + 4, y: s.y - LABEL_H / 2, w: v.w, h: LABEL_H, name: '', num: v.num, full: `${pairTitle(e)}${SEP}${vs[3]!.num}`, variant: 4, onLine: true };
      placed.push(e.label);
    }
  }
}

interface Seg { x0: number; y0: number; x1: number; y1: number; horizontal: boolean; len: number; box: Rect }
/** The straight segments between corner points, with a thin box each. */
export function segments(pts: Pt[]): Seg[] {
  const out: Seg[] = [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const horizontal = Math.abs(a.y - b.y) < 0.5;
    out.push({
      x0: a.x, y0: a.y, x1: b.x, y1: b.y, horizontal, len: Math.hypot(b.x - a.x, b.y - a.y),
      box: { x: Math.min(a.x, b.x) - 1.5, y: Math.min(a.y, b.y) - 1.5, w: Math.abs(b.x - a.x) + 3, h: Math.abs(b.y - a.y) + 3 },
    });
  }
  return out;
}

// ---------------------------------------------------------------- the checker

/**
 * What overlaps in a layout, as readable lines (empty: nothing): cards on cards, labels on labels or cards, a line
 * through a card, two lines on one stretch of track (vertical or horizontal), a label on another line, anything outside.
 */
export function problems(l: SysLayout): { kind: 'node-node' | 'label-label' | 'label-node' | 'edge-node' | 'shared-track' | 'label-edge' | 'outside'; what: string }[] {
  const out: ReturnType<typeof problems> = [];
  const inside = (r: Rect) => r.x >= -0.5 && r.y >= -0.5 && r.x + r.w <= l.width + 0.5 && r.y + r.h <= l.height + 0.5;
  for (const [i, a] of l.nodes.entries()) {
    if (!inside(a)) out.push({ kind: 'outside', what: a.code });
    for (const b of l.nodes.slice(i + 1)) if (hits(a, b)) out.push({ kind: 'node-node', what: `${a.code} / ${b.code}` });
  }
  for (const [i, e] of l.edges.entries()) {
    const lb = e.label;
    if (!inside(lb)) out.push({ kind: 'outside', what: `label ${e.key}` });
    for (const f of l.edges.slice(i + 1)) if (hits(lb, f.label)) out.push({ kind: 'label-label', what: `${e.key} / ${f.key}` });
    for (const n of l.nodes) if (hits(lb, n)) out.push({ kind: 'label-node', what: `${e.key} on ${n.code}` });
    for (const f of l.edges) {
      if (f === e) continue;
      for (const s of segments(f.pts)) if (hits(lb, s.box)) { out.push({ kind: 'label-edge', what: `label ${e.key} on ${f.key}` }); break; }
    }
    for (const s of segments(e.pts)) {
      for (const n of l.nodes) {
        // the interior of the card: a line may start and end at its sides
        const core = { x: n.x + 1, y: n.y + 1, w: n.w - 2, h: n.h - 2 };
        const box = { x: Math.min(s.x0, s.x1), y: Math.min(s.y0, s.y1), w: Math.abs(s.x1 - s.x0), h: Math.abs(s.y1 - s.y0) };
        if (box.x < core.x + core.w && core.x < box.x + Math.max(box.w, 0.01) && box.y < core.y + core.h && core.y < box.y + Math.max(box.h, 0.01)) {
          out.push({ kind: 'edge-node', what: `${e.key} through ${n.code}` });
        }
      }
    }
  }
  // Two edges running over each other: collinear segments that share more than a point
  const segs = l.edges.flatMap((e) => segments(e.pts).map((s) => ({ ...s, key: e.key })));
  for (let i = 0; i < segs.length; i++) for (let j = i + 1; j < segs.length; j++) {
    const a = segs[i]!;
    const b = segs[j]!;
    if (a.key === b.key || a.horizontal !== b.horizontal) continue;
    const [pa, pb] = a.horizontal ? [a.y0, b.y0] : [a.x0, b.x0];
    if (Math.abs(pa - pb) >= 2) continue;
    const [a0, a1] = a.horizontal ? [Math.min(a.x0, a.x1), Math.max(a.x0, a.x1)] : [Math.min(a.y0, a.y1), Math.max(a.y0, a.y1)];
    const [b0, b1] = a.horizontal ? [Math.min(b.x0, b.x1), Math.max(b.x0, b.x1)] : [Math.min(b.y0, b.y1), Math.max(b.y0, b.y1)];
    if (Math.min(a1, b1) - Math.max(a0, b0) > 1) out.push({ kind: 'shared-track', what: `${a.key} / ${b.key} ${a.horizontal ? 'y' : 'x'}=${Math.round(pa)}` });
  }
  return out;
}
